export const CURRENCY_SYMBOLS = { GBP: '£', USD: '$', EUR: '€', NOK: 'kr', SGD: 'S$', AED: 'د.إ' };
export const getCurrencySymbol = (code) => CURRENCY_SYMBOLS[code] || code;

/** Derive Q&A toggle fields from stored trip data (for edit mode) */
export const deriveQAFields = (trip) => {
  const allPreStops = [...(trip.pre_uk_stops || []), ...(trip.pre_abroad_stops || [])];
  const preHotelStop = allPreStops.find(s => s.type === 'hotel' || s.type === 'both');
  const preTravelStop = allPreStops.find(s => s.type === 'travel');

  const allPostStops = [...(trip.post_abroad_stops || []), ...(trip.post_uk_stops || [])];
  const postHotelStop = allPostStops.find(s => s.type === 'hotel' || s.type === 'both');
  const postTravelStop = allPostStops.find(s => s.type === 'travel');

  const hadPreTravel = trip.pre_travel || !!trip.pre_departure_date || !!preTravelStop;
  const hadPostTravel = trip.post_travel || !!trip.post_return_date || !!postTravelStop;

  return {
    pre_had_travel: hadPreTravel || undefined,
    pre_travel_abroad: hadPreTravel ? !!trip.pre_departure_date : undefined,
    pre_travel_route: preTravelStop?.description || '',
    pre_had_hotel: preHotelStop ? true : undefined,
    pre_hotel_abroad: (trip.pre_abroad_stops || []).some(s => s.type === 'hotel' || s.type === 'both'),
    pre_hotel_name: preHotelStop?.hotel_name || '',
    pre_hotel_location: preHotelStop?.location || '',
    pre_hotel_checkin: preHotelStop?.date || '',
    pre_hotel_checkout: preHotelStop?.checkout_date || '',
    post_had_travel: hadPostTravel || undefined,
    post_travel_abroad: hadPostTravel ? !!trip.post_return_date : undefined,
    post_travel_route: postTravelStop?.description || '',
    post_had_hotel: postHotelStop ? true : undefined,
    post_hotel_abroad: (trip.post_abroad_stops || []).some(s => s.type === 'hotel' || s.type === 'both'),
    post_hotel_name: postHotelStop?.hotel_name || '',
    post_hotel_location: postHotelStop?.location || '',
    post_hotel_checkin: postHotelStop?.date || '',
    post_hotel_checkout: postHotelStop?.checkout_date || '',
  };
};

/** Build backend-compatible stops payload from Q&A fields */
export const buildStopsPayload = (formData) => {
  const preUkStops = [];
  const preAbroadStops = [];
  const postUkStops = [];
  const postAbroadStops = [];

  // Pre-trip hotel
  if (formData.pre_had_hotel) {
    const stop = { type: 'hotel', hotel_name: formData.pre_hotel_name || '', location: formData.pre_hotel_location || '', date: formData.pre_hotel_checkin || '', checkout_date: formData.pre_hotel_checkout || '' };
    if (formData.pre_hotel_abroad) preAbroadStops.push(stop); else preUkStops.push(stop);
  }
  // Pre-trip travel route
  if (formData.pre_had_travel && formData.pre_travel_route) {
    const stop = { type: 'travel', description: formData.pre_travel_route, date: formData.pre_departure_date || '' };
    if (formData.pre_travel_abroad) preAbroadStops.push(stop); else preUkStops.push(stop);
  }
  // Post-trip hotel
  if (formData.post_had_hotel) {
    const stop = { type: 'hotel', hotel_name: formData.post_hotel_name || '', location: formData.post_hotel_location || '', date: formData.post_hotel_checkin || '', checkout_date: formData.post_hotel_checkout || '' };
    if (formData.post_hotel_abroad) postAbroadStops.push(stop); else postUkStops.push(stop);
  }
  // Post-trip travel route
  if (formData.post_had_travel && formData.post_travel_route) {
    const stop = { type: 'travel', description: formData.post_travel_route, date: formData.post_return_date || '' };
    if (formData.post_travel_abroad) postAbroadStops.push(stop); else postUkStops.push(stop);
  }

  return {
    pre_travel: !!(formData.pre_had_travel || formData.pre_had_hotel),
    pre_departure_date: (formData.pre_had_travel && formData.pre_travel_abroad && formData.pre_departure_date) ? formData.pre_departure_date : null,
    has_pre_uk: preUkStops.length > 0, pre_uk_stops: preUkStops,
    has_pre_abroad: preAbroadStops.length > 0, pre_abroad_stops: preAbroadStops,
    post_travel: !!(formData.post_had_travel || formData.post_had_hotel),
    post_return_date: (formData.post_had_travel && formData.post_travel_abroad && formData.post_return_date) ? formData.post_return_date : null,
    has_post_abroad: postAbroadStops.length > 0, post_abroad_stops: postAbroadStops,
    has_post_uk: postUkStops.length > 0, post_uk_stops: postUkStops,
  };
};

/** Initial form state */
export const getInitialFormData = () => ({
  trip_type: 'work_trip',
  pay_type: 'day_rate',
  uk_waters_status: 'outside',
  date_left_uk: '',
  date_returned_uk: '',
  no_uk_departure: false,
  no_uk_return: false,
  date_joined_vessel: '',
  date_left_vessel: '',
  vessel_name: '',
  contractor: '',
  port_joining: '',
  port_leaving: '',
  foreign_ports: '',
  hotel_name: '',
  hotel_city: '',
  hotel_reason: '',
  course_name: '',
  course_provider: '',
  course_location: '',
  has_multiple_uk_crossings: false,
  notes: '',
  leave_type: 'annual_leave',
  adjustment_type: 'addition',
  adjustment_description: '',
  adjustment_amount: '',
  currency: 'GBP',
  actual_gbp_received: '',
  monthly_salary: '',
  sea_uplift_rate: '',
  base_name: '',
  base_location: '',
  pre_travel: false,
  pre_departure_date: '',
  has_pre_uk: false,
  pre_uk_stops: [],
  has_pre_abroad: false,
  pre_abroad_stops: [],
  has_post_abroad: false,
  post_abroad_stops: [],
  post_travel: false,
  post_return_date: '',
  has_post_uk: false,
  post_uk_stops: [],
  pre_had_travel: undefined,
  pre_travel_abroad: undefined,
  pre_travel_route: '',
  pre_had_hotel: undefined,
  pre_hotel_abroad: false,
  pre_hotel_name: '',
  pre_hotel_location: '',
  pre_hotel_checkin: '',
  pre_hotel_checkout: '',
  post_had_travel: undefined,
  post_travel_abroad: undefined,
  post_travel_route: '',
  post_had_hotel: undefined,
  post_hotel_abroad: false,
  post_hotel_name: '',
  post_hotel_location: '',
  post_hotel_checkin: '',
  post_hotel_checkout: '',
  has_separate_travel_rate: false,
  travel_day_rate: '',
  travel_uplift_rate: '',
  has_separate_hotel_rate: false,
  hotel_day_rate: '',
  hotel_uplift_rate: '',
  contract_id: ''
});
